import { calculateDistance, calculateBearing, toRad, toDeg } from "../../utils/calculateDistance";


export const MIN_MOVEMENT_THRESHOLD = 5; // 5 meters
export const STOPPED_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes in ms
export const STATUS_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes in ms
export const MAX_HISTORY_LENGTH = 5; // Store last 5 positions
export const PASSED_DISTANCE_THRESHOLD = 200;
export const STOP_PROXIMITY_THRESHOLD = 0.2; // 200 meters threshold for "at stop"
export const STOP_LEFT_THRESHOLD = 0.3; // 300 meters threshold for "left from stop"


const CONSTANTS = {
  EARTH_RADIUS: 6371e3,
  AVG_SPEED: 25,
  TRAFFIC_MULTIPLIER: 1.1,
  MIN_STOP_TIME: 10,
  MAX_STOP_TIME: 6000,
  HISTORY_POINTS: 15,
  MIN_TIME_DIFF: 10,
  MAX_SPEED_THRESHOLD: 100,
  MIN_DISTANCE_THRESHOLD: 10,
  SPEED_UPDATE_INTERVAL: 300000,
  EMA_WEIGHT: 0.8,
  REACHED_DISTANCE_THRESHOLD: 0.1, // 100 meters
  STOP_PROXIMITY_THRESHOLD: 0.2, // 200 meters
};

export const parseCoordinate = (v) =>
  typeof v === "string" ? parseFloat(v) : isNaN(v) ? null : v;

export const hasValidCoordinates = (stop) =>
  parseCoordinate(stop.latitude) !== null &&
  parseCoordinate(stop.longitude) !== null; export const processRouteCoordinates = (route) =>
    (route.routeCoordinates || [])
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((coord) => ({
        lat: parseCoordinate(coord.latitude) || 0,
        lng: parseCoordinate(coord.longitude) || 0,
        stopName: coord.name || coord.stopId || "",
        stopId: coord.stopId,
      }));




export const processUniqueRoutes = (stops) => {
  const map = new Map();
  stops.forEach((stop) => {
    stop.routes?.forEach((route) => {
      if (!map.has(route.id)) {
        map.set(route.id, {
          id: route.id,
          name: route.name || `Route ${route.id}`,
          color: route.routeColor || "#20A8E0",
          coordinates: processRouteCoordinates(route),
          routeCoordinates: route.routeCoordinates || [],
          assignedVehicles: route.vehicles || []
        });
      }
    });
  });
  return Array.from(map.values());
};

export const extractAllVehicles = (stops) => {
  const vehicles = [];
  stops.forEach((stop) => {
    stop.routes?.forEach((route) => {
      route.vehicles?.forEach((vehicle) => {
        if (!vehicles.some((v) => v.device_id === vehicle.device_id)) {
          vehicles.push({
            device_id: vehicle.device_id,
            id: vehicle.id,
            name: vehicle.name,
            routeId: route.id,
            color: vehicle.vehicle_color,
            status: vehicle.status,
          });
        }
      });
    });
  });
  return vehicles;
};


export const getDestinationStop = (route, direction, stopsData) => {
  if (!route?.routeCoordinates?.length) return "";
  let coord;
  if (direction === "source_to_dest") {
    coord = route.routeCoordinates.at(-1);
  } else if (direction === "dest_to_source") {
    coord = route.routeCoordinates[0];
  }
  if (!coord) return "";

  const stop = stopsData.find(
    (s) =>
      s.id === coord.stopId ||
      (Math.abs(
        parseFloat(s.latitude) - parseFloat(coord.lat || coord.latitude)
      ) < 0.0002 &&
        Math.abs(
          parseFloat(s.longitude) - parseFloat(coord.lng || coord.longitude)
        ) < 0.0002)
  );

  return (
    stop?.name ||
    coord.stopName ||
    coord.name ||
    coord.stopId ||
    (direction === "source_to_dest" ? "Destination" : "Source")
  );
};

export const vehiclesVisitingStop = (stop, vehicles, routes) =>
  vehicles.filter((vehicle) => {
    const route = routes.find((r) => r.id === vehicle.routeId);
    if (!route?.routeCoordinates?.length) return false;
    return route.routeCoordinates.some(
      (coord) =>
        (coord.stopId && coord.stopId === stop.id) ||
        (Math.abs(parseFloat(coord.latitude) - parseFloat(stop.latitude)) <
          0.0002 &&
          Math.abs(parseFloat(coord.longitude) - parseFloat(stop.longitude)) <
          0.0002)
    );
  });


export const getClosestPointOnRoute = (vehiclePosition, routeCoordinates) => {
  if (!vehiclePosition || !routeCoordinates || routeCoordinates.length === 0) {
    return { index: -1, distance: Infinity };
  }

  let closestIndex = -1;
  let minDistance = Infinity;

  for (let i = 0; i < routeCoordinates.length; i++) {
    const routeCoord = routeCoordinates[i];
    const point = {
      latitude: parseFloat(routeCoord.lat || routeCoord.latitude),
      longitude: parseFloat(routeCoord.lng || routeCoord.longitude),
    };
    if (isNaN(point.latitude) || isNaN(point.longitude)) continue;

    const dist = calculateDistance(vehiclePosition, point);
    if (dist !== null && dist < minDistance) {
      minDistance = dist;
      closestIndex = i;
    }
  }
  return { index: closestIndex, distance: minDistance };
};

export const getVehicleStatusColor = (vehicle) => {
  const { status } = vehicle;

  if (status === 'DEAD') {
    return {
      bgColor: '#F9E719',
      textColor: '#FFFFFF',
      statusText: 'Offline'
    };
  }

  if (status === 'INACTIVE') {
    return {
      bgColor: '#F9E719',
      textColor: '#374151',
      statusText: 'Inactive'
    };
  }

  if (status === 'ACTIVE') {
    return {
      bgColor: '#008C23',
      textColor: '#FFFFFF',
      statusText: 'Active'
    };
  }

  // fallback (in case status is undefined or unexpected)
  return {
    bgColor: '#9CA3AF', // neutral gray
    textColor: '#FFFFFF',
    statusText: 'Unknown'
  };
};


export const getDirectionalDistance = (vehicle, stop, route) => {
  if (!route?.routeCoordinates?.length) return null;

  // Find closest index of vehicle on the route
  const vehicleIndex = route.routeCoordinates.findIndex(coord =>
    Math.abs(parseFloat(coord.lat) - vehicle.position.lat) < 0.0002 &&
    Math.abs(parseFloat(coord.lng) - vehicle.position.lng) < 0.0002
  );

  // Find closest index of stop on the route
  const stopIndex = route.routeCoordinates.findIndex(coord =>
    Math.abs(parseFloat(coord.lat) - stop.latitude) < 0.0002 &&
    Math.abs(parseFloat(coord.lng) - stop.longitude) < 0.0002
  );

  if (vehicleIndex === -1 || stopIndex === -1) return null;

  let distance = 0;

  if (vehicle.direction === "source_to_dest") {
    if (stopIndex >= vehicleIndex) {
      // stop is ahead → calculate forward distance
      for (let i = vehicleIndex; i < stopIndex; i++) {
        distance += calculateDistance(
          { lat: route.routeCoordinates[i].lat, lng: route.routeCoordinates[i].lng },
          { lat: route.routeCoordinates[i + 1].lat, lng: route.routeCoordinates[i + 1].lng }
        );
      }
    } else {
      // stop is behind → means vehicle will reach it on return trip
      // distance = vehicle → route end → return to stop
      for (let i = vehicleIndex; i < route.routeCoordinates.length - 1; i++) {
        distance += calculateDistance(
          { lat: route.routeCoordinates[i].lat, lng: route.routeCoordinates[i].lng },
          { lat: route.routeCoordinates[i + 1].lat, lng: route.routeCoordinates[i + 1].lng }
        );
      }
      for (let i = route.routeCoordinates.length - 1; i > stopIndex; i--) {
        distance += calculateDistance(
          { lat: route.routeCoordinates[i].lat, lng: route.routeCoordinates[i].lng },
          { lat: route.routeCoordinates[i - 1].lat, lng: route.routeCoordinates[i - 1].lng }
        );
      }
    }
  } else if (vehicle.direction === "dest_to_source") {
    if (stopIndex <= vehicleIndex) {
      // stop is ahead in reverse direction
      for (let i = vehicleIndex; i > stopIndex; i--) {
        distance += calculateDistance(
          { lat: route.routeCoordinates[i].lat, lng: route.routeCoordinates[i].lng },
          { lat: route.routeCoordinates[i - 1].lat, lng: route.routeCoordinates[i - 1].lng }
        );
      }
    } else {
      // stop is behind → need full loop to return
      for (let i = vehicleIndex; i > 0; i--) {
        distance += calculateDistance(
          { lat: route.routeCoordinates[i].lat, lng: route.routeCoordinates[i].lng },
          { lat: route.routeCoordinates[i - 1].lat, lng: route.routeCoordinates[i - 1].lng }
        );
      }
      for (let i = 0; i < stopIndex; i++) {
        distance += calculateDistance(
          { lat: route.routeCoordinates[i].lat, lng: route.routeCoordinates[i].lng },
          { lat: route.routeCoordinates[i + 1].lat, lng: route.routeCoordinates[i + 1].lng }
        );
      }
    }
  }

  return distance; // in KM
};


export const calculateDistanceBetweenStopAndVehicle = (vehiclePos, stopPos) => {
  if (
    !vehiclePos?.lat ||
    !vehiclePos?.lng ||
    !stopPos?.latitude ||
    !stopPos?.longitude
  ) {
    return null;
  }

  try {
    const φ1 = (vehiclePos.lat * Math.PI) / 180;
    const φ2 = (parseFloat(stopPos.latitude) * Math.PI) / 180;
    const Δφ =
      ((parseFloat(stopPos.latitude) - vehiclePos.lat) * Math.PI) / 180;
    const Δλ =
      ((parseFloat(stopPos.longitude) - vehiclePos.lng) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    const distanceInMeters = CONSTANTS.EARTH_RADIUS * c;
    return distanceInMeters / 1000;
  } catch (error) {
    console.error("Error calculating distance:", error);
    return null;
  }
};


export const calculateDistanceBetweenPoints = (point1, point2) => {
  if (!point1?.lat || !point1?.lng || !point2?.lat || !point2?.lng) {
    return null;
  }

  const φ1 = (point1.lat * Math.PI) / 180;
  const φ2 = (point2.lat * Math.PI) / 180;
  const Δφ = ((point2.lat - point1.lat) * Math.PI) / 180;
  const Δλ = ((point2.lng - point1.lng) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return CONSTANTS.EARTH_RADIUS * c;
};

export const formatTime = (timeInSeconds) => {
  if (!timeInSeconds || timeInSeconds <= 0) return "Loading...";

  const seconds = Math.round(timeInSeconds);
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
};

export const calculateHaversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in kilometers
  const toRad = (deg) => deg * (Math.PI / 180);

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c;

  return distance;
};


  export const getStatusPriority = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 1;
      case 'INACTIVE':
        return 2;
      case 'DEAD':
        return 3;
      default:
        return 4;
    }
  };


  export const getAppSettingsFromLocalStorage = () => {
  try {
    const appSettingsString = localStorage.getItem("appSettings");
    if (appSettingsString) {
      return JSON.parse(appSettingsString);
    }
  } catch (error) {
    console.error("Error parsing app settings from localStorage:", error);
  }
  
  // Return default values if localStorage is empty or invalid
  return {
    vehiclesCount: 5, // Default to showing all vehicles
    vehiclesStatus: ["active", "inactive", "dead"] // Default to showing all statuses
  };
};



